import React, { useCallback, useEffect, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import LogoUrl from "../assets/CM-Logo-Colour.png";
import PartModal from "@/PartModal";
import {
  initialize,
  selectAllServicesInitialized,
  selectServiceErrors,
} from "@/store/features/servicesSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import styles from "./ServiceInitializer.module.scss";

interface ServiceInitializerProps {
  children: React.ReactNode;
}

const CSSTransitionClasses = {
  enter: styles.transitionEnter,
  enterActive: styles.transitionEnterActive,
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
};

export const ServiceInitializer: React.FC<ServiceInitializerProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const allInitialized = useAppSelector(selectAllServicesInitialized);
  const errors = useAppSelector(selectServiceErrors);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await dispatch(initialize()).unwrap();
      } catch (error) {
        console.error("Service initialization failed:", error);
      } finally {
        setIsInitializing(false);
      }
    })();
  }, [dispatch]);

  const nodeRef1 = React.useRef(null);
  const nodeRef2 = React.useRef(null);
  const nodeRef3 = React.useRef(null);
  const nodeRef4 = React.useRef(null);

  return (
    <TransitionGroup className={styles.serviceInitializer}>
      {(isInitializing && (
        <CSSTransition
          key="loading"
          timeout={500}
          unmountOnExit
          nodeRef={nodeRef1}
          classNames={CSSTransitionClasses}
        >
          <div ref={nodeRef1}>
            <LoadingScreen />
          </div>
        </CSSTransition>
      )) ||
        (errors.length > 0 && (
          <CSSTransition
            key="error"
            timeout={500}
            unmountOnExit
            nodeRef={nodeRef2}
            classNames={CSSTransitionClasses}
          >
            <div ref={nodeRef2}>
              <ErrorScreen errors={errors} />
            </div>
          </CSSTransition>
        )) ||
        (!allInitialized && (
          <CSSTransition
            key="partial"
            timeout={500}
            unmountOnExit
            nodeRef={nodeRef3}
            classNames={CSSTransitionClasses}
          >
            <div ref={nodeRef3}>
              <PartiallyInitializedScreen />
            </div>
          </CSSTransition>
        )) || (
          <CSSTransition
            key="initialized"
            timeout={500}
            unmountOnExit
            nodeRef={nodeRef4}
            classNames={CSSTransitionClasses}
          >
            <div ref={nodeRef4}>{children}</div>
          </CSSTransition>
        )}
    </TransitionGroup>
  );
};

// Helper components
const LoadingScreen: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const nodeRef = React.useRef<HTMLImageElement | null>(null);
  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  // Effect to check if the image is already loaded when the component mounts
  useEffect(() => {
    // If the image is already loaded in the cache, trigger the fade-in immediately
    if (nodeRef.current?.complete) {
      setIsLoaded(true);
    }
  }, [LogoUrl.src]);

  return (
    <>
      <style>{`
  body {
    overflow-y: scroll;
    margin: 0;
  }
  .loader {
    min-height: calc(100vh - 80px);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loader > * {
    width: auto;
    height: 85px;
    -webkit-animation: 4s ease-in-out infinite pulse_preload;
    animation: 4s ease-in-out infinite pulse_preload;
  }

  @-webkit-keyframes pulse_preload {
    0%,
    100% {
      -webkit-transform: scale(1);
      transform: scale(1);
    }

    50% {
      -webkit-transform: scale(1.2);
      transform: scale(1.2);
    }
  }

  @keyframes pulse_preload {
    0%,
    100% {
      -webkit-transform: scale(1);
      transform: scale(1);
    }

    50% {
      -webkit-transform: scale(1.2);
      transform: scale(1.2);
    }
  }
    `}</style>
      <div className="loader">
        <img
          ref={nodeRef}
          src={LogoUrl.src}
          alt="Logo"
          onLoad={handleImageLoad} // Trigger when the image is loaded
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.5s ease", // Apply the fade-in effect
          }}
        />
      </div>
    </>
  );
};

const ErrorScreen: React.FC<{
  errors: Array<{ service: string; error: string | null }>;
}> = ({ errors }) => (
  // TODO Create good design
  <>
    <div>
      {/* This hack is critically needed for proper work back shadow in PartModal */}
      &nbsp;
    </div>
    <PartModal title="Initialization Error">
      <div className={styles.errorsContainer}>
        {errors.map(({ service, error }) => (
          <div key={service}>
            ❌ {service}: {error}
          </div>
        ))}
      </div>
      <div className={styles.buttonContainer}>
        <button
          className={styles.button}
          onClick={() => globalThis.window.location.reload()}
        >
          Reload page
        </button>
      </div>
    </PartModal>
  </>
);

const PartiallyInitializedScreen: React.FC = () => (
  // TODO Create good design
  <div>
    <h2>Some services failed to initialize</h2>
    {/* Add retry button or other recovery options */}
  </div>
);
